import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Checkbox } from "@material-ui/core";
import Icon from "../../../utils/icons";
import { formatScenes,formatSchedulerTime, generateHourArray, formatTime, formatHHMM, snap, getDefaultSchedulerData, frameSchedulerData, frameSchedulerDataForSaving } from './utils/helpers'
import { HOURS, ROW_HEIGHT, DAYS, BLOCK_GAP, SCENES, PAGES, MODES } from '../../../utils/constant'
import Tooltip from './utils/Tooltip'
import I18n from "../../../localization";
import Dropdown from './utils/dropdown'
import { useMobileStyles } from "./styles";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import HCServices from '../../../services/hcServices';
import { Header, PopUp } from '../../../components';
import { getGlobalState } from '../../../utils/globalState';
import { isiOS, convertUnit, airRoundOff, getAirUnit } from '../../../utils/utility';
import ScheduleDetailsView from './components/ScheduleDetailsView';

function getBlockTempDisplay(block, presets) {
  const preset = presets.find(p => (p.name || '').toLowerCase() === (block.name || '').toLowerCase());
  if (!preset) return '';
  const fmt = (t) => (t === null || t === undefined) ? '-' : `${airRoundOff(convertUnit(t))}`;
  const tempMode = preset['temperature-mode'] || MODES.AUTO;
  if (tempMode === MODES.OFF) return '-';
  const unit = getAirUnit() === 'C' ? '°C' : '°F';
  if (tempMode === MODES.HEAT) return `${fmt(preset['heat-setpoint'])}${unit}`;
  if (tempMode === MODES.COOL) return `${fmt(preset['cool-setpoint'])}${unit}`;
  return `${fmt(preset['heat-setpoint'])}°-${fmt(preset['cool-setpoint'])}${unit}`;
}

function SortableBlockCard({ block, index, mode, selectedBlockIndex, setSelectedBlockIndex, deleteSingleScene, classes, presets = [] }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isReorderMode = mode === 'reorder';

  return (
    <div
      ref={isReorderMode ? setNodeRef : undefined}
      style={isReorderMode ? style : undefined}
      className={`${classes.editBlockCard} 
        ${isDragging ? classes.dragging : ""}
        ${isReorderMode ? classes.reorderMode : ""}
        ${selectedBlockIndex === index ? (isReorderMode ? classes.reorderActiveCard : classes.activeCard) : ''}
      `}
      {...(isReorderMode ? { ...attributes, ...listeners } : {})}
      onClick={() => {
        if (isDragging) return;
        if (mode === 'delete') return;
        if (isReorderMode) {
          setSelectedBlockIndex(index);
          return;
        }
        setSelectedBlockIndex((prev) =>
          prev === index ? null : index
        );
      }}
    >
      {mode === 'delete' && (
        <div
          className={classes.deleteBadge}
          onClick={(e) => {
            e.stopPropagation();
            deleteSingleScene(index);
          }}
        >
          −
        </div>
      )}
      <div
        className={classes.editBlockColor}
        style={{ background: block.color }}
      />
      <div className={classes.editBlockName}>{block.name}</div>
      {presets.length > 0 && (
        <div className={classes.editBlockTemp}>{getBlockTempDisplay(block, presets)}</div>
      )}
    </div>
  );
}

export default function IQAirScheduler({
  scheduleData = null,
  isDarkMode = false,
  presets = [],
  isReadOnlyMode = false,
  onScheduleDataChange,
  showPopupHeader = false,
  popupTitle = I18n.t("mobile.iqAirScheduler.editSchedule"),
  onClosePopup = null,
  handleClick,
  siteId,
  iqAirId,
  isStandalonePage = false,
  isWeb = false,
  airDeviceName = '',
  updateHeaderText,
  initialSelectedDays = [],
  onSave,
  originalData,
  currentEnergyMode,
  isAddMode: isAddModeProp = false,
  hasAccess = true,
  maskedOwnerEmail
}) {
  const classes = useMobileStyles();
  const timelineRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [sourceDay, setSourceDay] = useState(initialSelectedDays.length > 0 ? initialSelectedDays[0] : null);
  const lastSourceDayRef = useRef(initialSelectedDays.length > 0 ? initialSelectedDays[0] : null);
  const [mode, setMode] = useState(null); // "add" | "delete" | "reorder" | null
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [selectedDays, setSelectedDays] = useState(initialSelectedDays);
  const [loader, setLoader] = useState(false);
  const [savingLoader, setSavingLoader] = useState(false);
  const [data, setData] = useState(scheduleData ? JSON.parse(JSON.stringify(scheduleData)) : null);
  const [localPresets, setLocalPresets] = useState(presets);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(initialSelectedDays.length > 0 ? 0 : null);
  const originalScheduleDataRef = useRef(scheduleData ? JSON.parse(JSON.stringify(scheduleData)) : null);
  const [standaloneHeaderName, setStandaloneHeaderName] = useState('');
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [popupScheduleData, setPopupScheduleData] = useState(null);
  const [editDayIndices, setEditDayIndices] = useState([]);
  const [isAddMode, setIsAddMode] = useState(false);
  const scheduleDetailsRef = useRef(null);
  const initialSelectedDaysRef = useRef(initialSelectedDays);
  const [showOverridePopup, setShowOverridePopup] = useState(false);
  const [overrideDays, setOverrideDays] = useState([]);
  const pendingSaveDataRef = useRef(null);
  const preOverrideSelectedDaysRef = useRef(null);
  const preOverrideDataRef = useRef(null);
  const hasChanges = useMemo(() => {
    const sortedCurrent = [...selectedDays].sort();
    const sortedInitial = [...initialSelectedDaysRef.current].sort();
    if (sortedCurrent.length !== sortedInitial.length || sortedCurrent.some((d, i) => d !== sortedInitial[i])) return true;
    if (!data || !originalScheduleDataRef.current) return false;
    const stripIds = (blocks) => blocks.map(({ id, ...rest }) => rest);
    for (const day of selectedDays) {
      const currentBlocks = stripIds(data[day] || []);
      const originalBlocks = stripIds(originalScheduleDataRef.current[day] || []);
      if (JSON.stringify(currentBlocks) !== JSON.stringify(originalBlocks)) return true;
    }
    return false;
  }, [data, selectedDays]);

  const setDataWithChange = (updater) => {
    setData(updater);
  };

  useEffect(() => {
    if (isWeb && updateHeaderText && isStandalonePage) {
      const name = standaloneHeaderName || airDeviceName || '';
      const type = standaloneHeaderName ? (airDeviceName || '') : '';
      updateHeaderText(
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{name}</span>
          {type && <span style={{ fontSize: '15px', color: 'rgb(111, 110, 142)', margin: '5px 0px', fontWeight: '400' }}>{type}</span>}
        </div>
      );
    }
  }, [isWeb, updateHeaderText, isStandalonePage, standaloneHeaderName, airDeviceName]);

  useEffect(() => {
    if (!isStandalonePage) return;

    const effectiveSiteId = siteId || getGlobalState('siteId');
    const effectiveIqAirId = iqAirId || getGlobalState('iqAirId');

    if (!effectiveSiteId || !effectiveIqAirId) return;

    const fetchData = async () => {
      setLoader(true);
      try {
        const response = await HCServices.getAirDeviceDetails(effectiveSiteId, effectiveIqAirId);

        if ((response.status === 'success' || response.status === 'SUCCESS') && response.data && response.data[0]) {
          const deviceData = response.data[0];

          setStandaloneHeaderName(deviceData.name || '');
          setLocalPresets(Array.isArray(deviceData?.presets) ? deviceData.presets : []);

          const customSchedules = deviceData?.['custom-schedules'];
          if (customSchedules && typeof customSchedules === 'object' && !Array.isArray(customSchedules)) {
            const normalizedSchedules = {
              monday: Array.isArray(customSchedules.monday) ? customSchedules.monday : [],
              tuesday: Array.isArray(customSchedules.tuesday) ? customSchedules.tuesday : [],
              wednesday: Array.isArray(customSchedules.wednesday) ? customSchedules.wednesday : [],
              thursday: Array.isArray(customSchedules.thursday) ? customSchedules.thursday : [],
              friday: Array.isArray(customSchedules.friday) ? customSchedules.friday : [],
              saturday: Array.isArray(customSchedules.saturday) ? customSchedules.saturday : [],
              sunday: Array.isArray(customSchedules.sunday) ? customSchedules.sunday : [],
            };
            setData(frameSchedulerData(normalizedSchedules, isDarkMode));
          } else {
            setData({});
          }
        } else {
          setData({});
        }
      } catch (err) {
        console.error('Error fetching scheduler data:', err);
        setData({});
      } finally {
        setLoader(false);
      }
    };

    fetchData();
  }, [isStandalonePage, siteId, iqAirId, isDarkMode]);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showStartDropdown, setShowStartDropdown] = useState(false);
  const [showEndDropdown, setShowEndDropdown] = useState(false);
  const plusRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const editBlocksRef = useRef(null);

  const handlePopupClose = () => {
    if (typeof onClosePopup === "function") {
      onClosePopup();
      return;
    }
    if (typeof handleClick === "function") {
      handleClick();
    }
  };

  const hasValidSetpoint = (value) => {
    if (value === null || value === undefined) return false;
    const numericValue = Number(value);
    return !Number.isNaN(numericValue) && numericValue !== 0;
  };

  const isOffTemperatureMode = (mode) => String(mode || '').toUpperCase() === 'OFF';

  const dropdownPresets = localPresets.filter((preset) => {
    if (isOffTemperatureMode(preset?.['temperature-mode'])) {
      return true;
    }

    return (
      hasValidSetpoint(preset?.['cool-setpoint']) ||
      hasValidSetpoint(preset?.['heat-setpoint'])
    );
  });


  // ✅ Tooltip state
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    text: "",
    dayIdx: null
  });




  // @dnd-kit sensors for reorder mode
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || sourceDay === null) return;

    setDataWithChange((prev) => {
      if (!prev?.[sourceDay]) return prev;
      const blocks = [...prev[sourceDay]];
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const reordered = arrayMove(blocks, oldIndex, newIndex);

      // Update selectedBlockIndex to follow the moved card
      setSelectedBlockIndex(newIndex);

      return {
        ...prev,
        [sourceDay]: normalizeBlocks(reordered)
      };
    });
  }, [sourceDay]);

  useEffect(() => {
    if (isReadOnlyMode || typeof onScheduleDataChange !== "function") {
      setData(scheduleData ?? null);
    }
  }, [scheduleData, isReadOnlyMode, onScheduleDataChange]);

  useEffect(() => {
    if (typeof onScheduleDataChange === "function" && data) {
      onScheduleDataChange(data);
    }
  }, [data, onScheduleDataChange]);

  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (mode === 'reorder') {
      setSelectedBlockIndex(0);
    } else if (prevModeRef.current !== null && prevModeRef.current !== 'add' && prevModeRef.current !== 'delete') {
      setSelectedBlockIndex(null);
    }
    if (mode !== 'delete') {
      setSelectedForDelete(new Set());
    }
    prevModeRef.current = mode;
  }, [mode])

  useEffect(() => {
    const blockCount = data?.[sourceDay]?.length || 0;
    if (blockCount === 1 && (mode === 'delete' || mode === 'reorder')) {
      setMode(null);
    }
  }, [data, sourceDay, mode]);

  useEffect(() => {

    if (!loader && data) {
      const updateWidth = () => {
        if (timelineRef.current) {
          setContainerWidth(timelineRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [loader, data]);

  useEffect(() => {
    if (data == null) return;
    if (sourceDay === null) { setMode(null); setSelectedBlockIndex(null); return };
    lastSourceDayRef.current = sourceDay;
    if (!selectedDays.length) return;

    setData((prev) => {
      if (prev == null || !prev?.[sourceDay]) return prev;
      const updated = { ...prev };

      selectedDays.forEach((day) => {
        if (day === sourceDay) return;

        updated[day] = prev[sourceDay].map((b) => ({
          ...b,
          id: `${b.name}_${Math.random()}`
        }));
      });

      return updated;
    });

  }, [selectedDays, sourceDay, data?.[sourceDay]]);

  const PX_PER_HOUR = containerWidth / HOURS;
  const timeToPx = (h) => h * PX_PER_HOUR;

  const updateBlockTime = (type, value) => {
    if (selectedBlockIndex === null || sourceDay === null) return;

    const MIN_DURATION = 0.5;
    const roundToHalfHour = (val) => Math.round(val * 2) / 2;

    setDataWithChange((prev) => {
      const updated = { ...prev };

      selectedDays.forEach((dayIdx) => {
        let blocks = [...(updated[dayIdx] || [])];

        const original = blocks.map((b) => ({ ...b }));
        const block = original[selectedBlockIndex];
        if (!block) return;

        const requestedValue = roundToHalfHour(value);

        if (type === "end") {
          if (selectedBlockIndex === blocks.length - 1) {
            blocks[selectedBlockIndex] = { ...blocks[selectedBlockIndex], end: 24 };
            updated[dayIdx] = blocks;
            return;
          }

          const trailingCount = blocks.length - selectedBlockIndex - 1;
          const minEnd = roundToHalfHour(block.start + MIN_DURATION);
          const maxEnd = roundToHalfHour(24 - trailingCount * MIN_DURATION);
          const newEnd = Math.max(minEnd, Math.min(requestedValue, maxEnd));

          blocks[selectedBlockIndex] = {
            ...blocks[selectedBlockIndex],
            end: newEnd
          };

          const oldEnd = block.end;

          if (newEnd <= oldEnd) {
            blocks[selectedBlockIndex + 1] = {
              ...blocks[selectedBlockIndex + 1],
              start: newEnd
            };
            updated[dayIdx] = blocks;
            return;
          }

          let carry = roundToHalfHour(newEnd - oldEnd);

          for (let k = selectedBlockIndex + 1; k < blocks.length; k++) {
            const oldStart = original[k].start;
            const oldEndK = original[k].end;
            const oldDuration = roundToHalfHour(oldEndK - oldStart);

            if (carry <= 0) {
              if (k === selectedBlockIndex + 1) {
                blocks[k] = { ...blocks[k], start: newEnd };
              }
              break;
            }

            const shrinkable = roundToHalfHour(oldDuration - MIN_DURATION);

            if (carry <= shrinkable) {
              blocks[k] = {
                ...blocks[k],
                start: roundToHalfHour(oldStart + carry),
                end: oldEndK
              };
              carry = 0;
              break;
            }

            const remaining = roundToHalfHour(carry - shrinkable);
            blocks[k] = {
              ...blocks[k],
              start: roundToHalfHour(oldEndK - MIN_DURATION + remaining),
              end: roundToHalfHour(oldEndK + remaining)
            };
            carry = remaining;
          }

          blocks[0] = { ...blocks[0], start: 0 };
          const lastIdx = blocks.length - 1;
          blocks[lastIdx] = { ...blocks[lastIdx], end: 24 };
        }

        if (type === "start") {
          if (selectedBlockIndex === 0) {
            blocks[selectedBlockIndex] = { ...blocks[selectedBlockIndex], start: 0 };
            updated[dayIdx] = blocks;
            return;
          }

          const leadingCount = selectedBlockIndex;
          const minStart = roundToHalfHour(leadingCount * MIN_DURATION);
          const maxStart = roundToHalfHour(block.end - MIN_DURATION);
          const newStart = Math.max(minStart, Math.min(requestedValue, maxStart));

          blocks[selectedBlockIndex] = {
            ...blocks[selectedBlockIndex],
            start: newStart
          };

          const oldStart = block.start;

          if (newStart >= oldStart) {
            blocks[selectedBlockIndex - 1] = {
              ...blocks[selectedBlockIndex - 1],
              end: newStart
            };
            updated[dayIdx] = blocks;
            return;
          }

          let carry = roundToHalfHour(oldStart - newStart);

          for (let k = selectedBlockIndex - 1; k >= 0; k--) {
            const oldStartK = original[k].start;
            const oldEndK = original[k].end;
            const oldDuration = roundToHalfHour(oldEndK - oldStartK);

            if (carry <= 0) {
              if (k === selectedBlockIndex - 1) {
                blocks[k] = { ...blocks[k], end: newStart };
              }
              break;
            }

            const shrinkable = roundToHalfHour(oldDuration - MIN_DURATION);

            if (carry <= shrinkable) {
              blocks[k] = {
                ...blocks[k],
                start: oldStartK,
                end: roundToHalfHour(oldEndK - carry)
              };
              carry = 0;
              break;
            }

            const remaining = roundToHalfHour(carry - shrinkable);
            blocks[k] = {
              ...blocks[k],
              start: roundToHalfHour(oldStartK - remaining),
              end: roundToHalfHour(oldStartK + MIN_DURATION - remaining)
            };
            carry = remaining;
          }

          blocks[0] = { ...blocks[0], start: 0 };
          const lastIdx = blocks.length - 1;
          blocks[lastIdx] = { ...blocks[lastIdx], end: 24 };
        }

        updated[dayIdx] = blocks;
      });

      return updated;
    });
  };



  const addSceneBlock = (scene) => {

    const dayIdx = sourceDay;

    setDataWithChange((prev) => {
      const blocks = [...(prev[dayIdx] || [])];

      if (blocks.length >= 6) return prev;
      const BLOCK_SIZE = 2;
      // 🔍 find block to shrink
      let shrinkIndex = -1;

      for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].end - blocks[i].start > BLOCK_SIZE) {
          shrinkIndex = i;
          break;
        }
      }

      if (shrinkIndex === -1) return prev;

      // 👉 ALWAYS use last block for placement
      const lastBlock = blocks[blocks.length - 1];
      const newEnd = lastBlock.end;
      const newStart = newEnd - BLOCK_SIZE;

      // 🔹 shrink chosen block
      blocks[shrinkIndex] = {
        ...blocks[shrinkIndex],
        end: blocks[shrinkIndex].end - BLOCK_SIZE
      };

      // 🔹 shift all blocks after shrinkIndex
      for (let i = shrinkIndex + 1; i < blocks.length; i++) {
        blocks[i] = {
          ...blocks[i],
          start: blocks[i].start - BLOCK_SIZE,
          end: blocks[i].end - BLOCK_SIZE
        };
      }

      // 🔹 add new block at END
      const newBlock = {
        id: `${scene.name.toLowerCase()}_${Date.now()}_${Math.random()}`,
        presetId: scene.presetId,
        start: newStart,
        end: newEnd,
        color: scene.color,
        name: scene.name
      };

      blocks.push(newBlock);

      setSelectedBlockIndex(blocks.length - 1);

      return {
        ...prev,
        [dayIdx]: blocks
      };
    });
  };



  const normalizeBlocks = (blocks) => {
    let currentStart = 0;

    return blocks.map((b) => {
      const duration = b.end - b.start;

      const newBlock = {
        ...b,
        start: currentStart,
        end: currentStart + duration
      };

      currentStart += duration;

      return newBlock;
    });
  };

  const deleteSingleScene = (blockIndex) => {
    setDataWithChange((prev) => {
      const updated = { ...prev };

      selectedDays.forEach((dayIdx) => {
        let blocks = [...(updated[dayIdx] || [])];

        if (blockIndex < 0 || blockIndex >= blocks.length) return;
        if (blocks.length === 1) return;

        const removed = blocks[blockIndex];

        // 👉 merge logic
        if (blockIndex > 0) {
          blocks[blockIndex - 1] = {
            ...blocks[blockIndex - 1],
            end: removed.end
          };
        } else {
          blocks[1] = {
            ...blocks[1],
            start: removed.start
          };
        }

        blocks.splice(blockIndex, 1);

        updated[dayIdx] = blocks;
      });

      return updated;
    });
    setSelectedBlockIndex((prev) => {
      if (prev === null) return 0;
      if (prev >= blockIndex && prev > 0) return prev - 1;
      return prev;
    });
  };

  const toggleDeleteSelection = (index) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const deleteSelectedScenes = () => {
    if (selectedForDelete.size === 0) { setMode(null); setSelectedForDelete(new Set()); return; }
    const indicesToDelete = [...selectedForDelete].sort((a, b) => b - a);
    setDataWithChange((prev) => {
      const updated = { ...prev };
      selectedDays.forEach((dayIdx) => {
        let blocks = [...(updated[dayIdx] || [])];
        indicesToDelete.forEach((blockIndex) => {
          if (blockIndex < 0 || blockIndex >= blocks.length || blocks.length === 1) return;
          const removed = blocks[blockIndex];
          if (blockIndex > 0) {
            blocks[blockIndex - 1] = { ...blocks[blockIndex - 1], end: removed.end };
          } else if (blocks.length > 1) {
            blocks[1] = { ...blocks[1], start: removed.start };
          }
          blocks.splice(blockIndex, 1);
        });
        updated[dayIdx] = blocks;
      });
      return updated;
    });
    setSelectedBlockIndex(0);
    setSelectedForDelete(new Set());
    setMode(null);
  };

  const sourceBlocks = Array.isArray(data?.[sourceDay])
    ? data[sourceDay]
    : Array.isArray(data?.[0])
      ? data[0]
      : [];
  const sourceBlockCount = sourceBlocks.length;
  const selectedSourceBlock = Number.isInteger(selectedBlockIndex) && selectedBlockIndex >= 0
    ? sourceBlocks[selectedBlockIndex]
    : null;
  const selectedStartTime = selectedSourceBlock?.start ?? 0;
  const selectedEndTime = selectedSourceBlock?.end ?? 0;

  return (
    <>
    {isStandalonePage && !isWeb && (
      <Header
        title={standaloneHeaderName || airDeviceName || ''}
        label={standaloneHeaderName ? (airDeviceName || '') : ''}
        onClick={() => {
          if (scheduleDetailsRef.current?.handleBackPress) {
            scheduleDetailsRef.current.handleBackPress();
          } else {
            handleClick(PAGES.IQ_AIR_SETTING);
          }
        }}
      />
    )}
    {isStandalonePage ? (
      <div className={`schedule-details-page-wrapper ${isDarkMode ? 'dark' : 'light'}${isWeb ? ' web' : ''}${isiOS() ? ' ios' : ''}`}>
        {(loader || data == null) ? (
          <div className={classes.loaderContainer}>
            <Icon src="loaderV4" width="60px" height="60px" />
          </div>
        ) : (
          <>
          <ScheduleDetailsView
            ref={scheduleDetailsRef}
            data={data}
            isDarkMode={isDarkMode}
            siteId={siteId || getGlobalState('siteId')}
            iqAirId={iqAirId || getGlobalState('iqAirId')}
            currentEnergyMode={currentEnergyMode}
            hasAccess={hasAccess}
            maskedOwnerEmail={maskedOwnerEmail}
            presets={localPresets}
            onNavigateBack={isStandalonePage ? () => handleClick(PAGES.IQ_AIR_SETTING) : handleClick}
            onScheduleClick={(dayIndices) => {
              setPopupScheduleData(JSON.parse(JSON.stringify(data)));
              setEditDayIndices(dayIndices || []);
              setIsAddMode(false);
              setShowEditPopup(true);
            }}
            onDeleteSchedule={(dayIndicesToClear) => {
              setData((prev) => {
                const updated = { ...prev };
                dayIndicesToClear.forEach((d) => {
                  updated[d] = [];
                });
                return updated;
              });
            }}
            onAddSchedule={() => {
              const coveredDays = new Set();
              for (let i = 0; i < 7; i++) {
                if (Array.isArray(data?.[i]) && data[i].length > 0) coveredDays.add(i);
              }
              const uncoveredDays = [0,1,2,3,4,5,6].filter(d => !coveredDays.has(d));
              if (uncoveredDays.length === 0) return;
              const findPreset = (name) => localPresets.find(p => (p.name || '').toLowerCase() === name.toLowerCase());
              const homePreset = findPreset('Home') || localPresets[0] || {};
              const awayPreset = findPreset('Away') || localPresets[1] || localPresets[0] || {};
              const nightPreset = findPreset('Night') || localPresets[2] || localPresets[0] || {};
              const makeBlock = (preset, start, end) => ({
                id: `${(preset.name || 'block').toLowerCase()}_${Date.now()}_${Math.random()}`,
                presetId: preset['preset-id'] || preset.presetId || preset.name,
                start,
                end,
                color: preset['light-color-code'] || preset.color || '#ccc',
                name: preset.name || 'Away',
              });
              const defaultBlocks = [
                makeBlock(homePreset, 0, 8),
                makeBlock(awayPreset, 8, 16),
                makeBlock(nightPreset, 16, 24),
              ];
              const newScheduleData = {};
              for (let i = 0; i < 7; i++) {
                newScheduleData[i] = uncoveredDays.includes(i) ? defaultBlocks.map(b => ({ ...b, id: `${b.name.toLowerCase()}_${Math.random()}` })) : (data?.[i] || []);
              }
              setPopupScheduleData(newScheduleData);
              setEditDayIndices(uncoveredDays);
              setIsAddMode(true);
              setShowEditPopup(true);
            }}
          />
          <PopUp
            isSchedulePopUp
            open={showEditPopup}
            handleClose={() => {
              setShowEditPopup(false);
            }}
            disableBackdropClick
            slideFromBottom
            fullWidth
            content={
              <IQAirScheduler
                isDarkMode={isDarkMode}
                showPopupHeader
                popupTitle={isAddMode ? I18n.t('mobile.iqAirScheduler.addSchedule', { defaultValue: 'Add Schedule' }) : I18n.t('mobile.iqAirScheduler.editSchedule')}
                onClosePopup={() => {
                  setShowEditPopup(false);
                }}
                scheduleData={popupScheduleData || data}
                presets={localPresets}
                onScheduleDataChange={setPopupScheduleData}
                originalData={data}
                onSave={(updatedData) => {
                  setData(updatedData);
                  setPopupScheduleData(updatedData);
                  setShowEditPopup(false);
                }}
                initialSelectedDays={editDayIndices}
                isAddMode={isAddMode}
                handleClick={() => {
                  setShowEditPopup(false);
                }}
              />
            }
          />
          </>
        )}
      </div>
    ) : (
    <div className={`${classes.schedulerWrapper} ${isReadOnlyMode ? classes.isReadOnlyMode : ''}`} >

      {(loader || data == null) ? <div className={classes.loaderContainer}>
        <Icon src="loaderV4" width="60px" height="60px" />
      </div> :
        <>
          {showPopupHeader && (
            <div className={classes.popupHeader}>
              <div className={classes.popupTitle}>{popupTitle}</div>
              <button
                type="button"
                className={classes.closeButton}
                onClick={handlePopupClose}
              >
                <Icon src="close" />
              </button>
            </div>
          )}
        <div className={classes.wrapper} style={isDarkMode ? { background: '#262627' } : undefined}>

            {/* Read-only mode: show all 7 day rows */}
            {isReadOnlyMode && (
              <>
                <div className={classes.pageTitle}>{I18n.t("mobile.iqAirScheduler.selectDayToEdit")}</div>
                {DAYS.map((d, dayIdx) => {
                  const isSource = sourceDay === dayIdx;
                  const isSelected = selectedDays.includes(dayIdx);

                  return (
                    <div key={dayIdx} className={classes.row}
                      onClick={() => {
                        setSelectedDays((prev) => {
                          let updated;
                          if (prev.includes(dayIdx)) {
                            updated = prev.filter((x) => x !== dayIdx);
                          } else {
                            updated = [...prev, dayIdx];
                          }
                          if (!updated.length) {
                            setSourceDay(null);
                          } else if (!sourceDay || !updated.includes(sourceDay)) {
                            setSourceDay(updated[0]);
                          }
                          return updated;
                        });
                      }}
                    >
                      <div
                        className={classes.dayLabel}
                        style={{
                          background: isSelected
                            ? "#01B5DE"
                            : isDarkMode ? "#262627" : isReadOnlyMode ? "#fff" : "#e0e0e0",
                          color: isSource || isSelected ? "#f8f8ff" : isDarkMode ? "#fff" : "#545456"
                        }}
                      >
                        {d}
                      </div>

                      <div className={classes.timeline} ref={dayIdx === 0 ? timelineRef : null} style={isDarkMode ? { background: '#262627' } : undefined}>
                        {[...Array(HOURS + 1)].map((_, i) => (
                          <div
                            key={i}
                            className={classes.hourLine}
                            style={{
                              left: timeToPx(i),
                              opacity: isReadOnlyMode ? (isDarkMode ? 1 : (i % 4 === 0 ? 0.5 : 0.6)) : 1,
                              borderLeftColor: isReadOnlyMode && i % 4 === 0
                                ? (isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#999')
                                : (isReadOnlyMode && isDarkMode ? 'rgba(255, 255, 255, 0.1)' : undefined),
                              borderLeftStyle: isReadOnlyMode && i % 4 !== 0 ? 'dotted' : 'solid',
                              top: isReadOnlyMode && i % 4 === 0 ? -6 : 0,
                              bottom: isReadOnlyMode && i % 4 === 0 ? -6 : 0,
                            }}
                          />
                        ))}

                        {(Array.isArray(data?.[dayIdx]) ? data[dayIdx] : []).map((b, i, arr) => {
                          const isFirst = i === 0;
                          const isLast = i === arr.length - 1;
                          const gapLeft = isFirst ? 0 : BLOCK_GAP / 2;
                          const gapRight = isLast ? 0 : BLOCK_GAP / 2;
                          const hasSelection = selectedDays.length > 0;
                          return (
                            <div
                              key={b.id}
                              className={`${classes.timelineBox} ${selectedBlockIndex === i && selectedDays.includes(dayIdx) ? classes.highlightBlock : ""}`}
                              style={{
                                left: timeToPx(b.start) + gapLeft,
                                width: Math.max(0, timeToPx(b.end - b.start) - (gapLeft + gapRight)),
                                height: (isReadOnlyMode ? 20 : ROW_HEIGHT) - 10,
                                background: b.color,
                                opacity: !hasSelection || isSelected ? 1 : 0.4,
                              }}
                            />
                          );
                        })}

                        {tooltip.visible && tooltip.dayIdx === dayIdx && <Tooltip tooltip={tooltip} containerWidth={containerWidth} classes={classes} />}
                      </div>
                    </div>
                  );
                })}

                <div className={classes.footerRow}>
                  <div style={{ width: isReadOnlyMode ? 16 : 20 }} />
                  <div className={classes.footerTimeline}>
                    {[0, 4, 8, 12, 16, 20, 24].map((t) => {
                      let num;
                      let suffix = '';
                      if (t === 0 || t === 24) { num = '12'; suffix = 'am'; }
                      else if (t === 12) { num = '12'; suffix = 'pm'; }
                      else if (t < 12) { num = t < 10 ? '0' + t : t + ''; }
                      else { num = (t - 12) < 10 ? '0' + (t - 12) : (t - 12) + ''; }
                      const transform = t === 0 ? 'translateX(0)' : t === 24 ? 'translateX(-100%)' : 'translateX(-50%)';
                      return (
                        <div
                          key={t}
                          style={{
                            position: "absolute",
                            left: timeToPx(t),
                            transform
                          }}
                        >
                          <div className={classes.xAxisLabel}><div>{num}{suffix && <div>{suffix}</div>}</div></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Edit/popup mode: single source day timeline at top */}
            {!isReadOnlyMode && (
              <>
                <div className={classes.editTimeBox} style={{ marginBottom: 8 }}>
                  <div className={classes.pageTitle}>{I18n.t('mobile.iqAirScheduler.dayPlan', { defaultValue: 'Day plan' })}</div>
                  <div className={classes.iconsBox}>
                    {/* ADD */}
                    <div
                      ref={plusRef}
                      className={`${mode === "add" ? classes.selectedModeIcon : ""} ${sourceBlockCount >= 6 ? classes.disableIcon : ""} ${classes.editTimeIcons}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (sourceBlockCount >= 6) return
                        setMode((prev) => (prev === "add" ? null : "add"));
                        setShowAddDropdown((prev) => !prev);
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke={sourceBlockCount >= 6 ? '#ccc' : (mode === 'add' ? '#fff' : '#00b4de')} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    {/* DELETE */}
                    <div
                      className={`${mode === "delete" && sourceBlockCount !== 1 ? classes.selectedModeIcon : "" } ${sourceBlockCount === 1 ? classes.disableIcon : ""} ${classes.editTimeIcons}`}
                      onClick={() => {
                        if (sourceBlockCount === 1) return;
                        setMode((prev) => (prev === "delete" ? null : "delete"))
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 10 13" fill="none">
                        <path d="M6.16113 0C6.98949 7.97531e-05 7.66113 0.671622 7.66113 1.5V1.66504H9.38086C9.65687 1.6652 9.88086 1.88899 9.88086 2.16504C9.88077 2.44101 9.65681 2.66488 9.38086 2.66504H8.68457C8.7399 2.8215 8.77148 2.98963 8.77148 3.16504V10.6006C8.7714 11.4288 8.09971 12.1004 7.27148 12.1006H2.61133C1.78296 12.1006 1.11142 11.4289 1.11133 10.6006V3.16504C1.11133 2.98955 1.14287 2.82156 1.19824 2.66504H0.5C0.224061 2.66486 8.84091e-05 2.441 0 2.16504C0 1.889 0.224006 1.66521 0.5 1.66504H2.2207V1.5C2.2207 0.671653 2.89239 0.000131287 3.7207 0H6.16113ZM2.61133 2.66504C2.33519 2.66504 2.11133 2.8889 2.11133 3.16504V10.6006C2.11142 10.8767 2.33524 11.1006 2.61133 11.1006H7.27148C7.54742 11.1004 7.7714 10.8765 7.77148 10.6006V3.16504C7.77148 2.88901 7.54748 2.66522 7.27148 2.66504H2.61133ZM3.83105 3.88574C4.10712 3.88583 4.33105 4.10965 4.33105 4.38574V9.38086C4.33092 9.65683 4.10704 9.88077 3.83105 9.88086C3.55499 9.88086 3.33119 9.65689 3.33105 9.38086V4.38574C3.33105 4.1096 3.55491 3.88574 3.83105 3.88574ZM6.05078 3.88574C6.32692 3.88574 6.55078 4.1096 6.55078 4.38574V9.38086C6.55065 9.65689 6.32684 9.88086 6.05078 9.88086C5.77472 9.88086 5.55091 9.65689 5.55078 9.38086V4.38574C5.55078 4.1096 5.77464 3.88574 6.05078 3.88574ZM3.7207 1C3.44467 1.00013 3.2207 1.22394 3.2207 1.5V1.66504H6.66113V1.5C6.66113 1.22391 6.43721 1.00008 6.16113 1H3.7207Z" fill={sourceBlockCount === 1 ? '#ccc' : (mode === 'delete' ? '#fff' : '#00b4de')} />
                      </svg>
                    </div>
                    {/* REORDER */}
                    <div
                      className={`${mode === "reorder" && sourceBlockCount !== 1 ? classes.selectedModeIcon : "" } ${sourceBlockCount === 1 ? classes.disableIcon : ""} ${classes.editTimeIcons}`}
                      onClick={() => {
                        if (sourceBlockCount === 1) return;
                        setMode((prev) => (prev === "reorder" ? null : "reorder"))
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 2v12M8 2v12M12 2v12" stroke={sourceBlockCount === 1 ? '#ccc' : (mode === 'reorder' ? '#fff' : '#00b4de')} strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div>
                  {showAddDropdown && mode === "add" && (
                    <Dropdown
                      classes={classes}
                      anchorRef={plusRef}
                      onClose={() => { setShowAddDropdown(false); setMode(null) }}
                      onSelect={(scene) => { addSceneBlock(scene); setMode(null) }}
                      data={formatScenes(dropdownPresets,isDarkMode)}
                    />
                  )}
                </div>
                <div className={classes.row} style={{ pointerEvents: 'none' }}>
                  <div className={classes.timeline} ref={timelineRef} style={isDarkMode ? { background: '#262627' } : undefined}>
                    {[...Array(HOURS + 1)].map((_, i) => (
                      <div
                        key={i}
                        className={classes.hourLine}
                        style={{
                          left: timeToPx(i),
                          top: 5,
                          bottom: -4,
                          borderLeft: i % 2 === 0
                            ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'}`
                            : `1px dotted ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
                        }}
                      />
                    ))}
                    {(Array.isArray(data?.[sourceDay !== null ? sourceDay : lastSourceDayRef.current]) ? data[sourceDay !== null ? sourceDay : lastSourceDayRef.current] : []).map((b, i, arr) => {
                      const isFirst = i === 0;
                      const isLast = i === arr.length - 1;
                      const gapLeft = isFirst ? 0 : BLOCK_GAP / 2;
                      const gapRight = isLast ? 0 : BLOCK_GAP / 2;
                      return (
                        <div
                          key={b.id}
                          className={`${classes.timelineBox} ${selectedBlockIndex === i ? classes.highlightBlock : ""}`}
                          style={{
                            left: timeToPx(b.start) + gapLeft,
                            width: Math.max(0, timeToPx(b.end - b.start) - (gapLeft + gapRight)),
                            height: ROW_HEIGHT - 10,
                            background: b.color,
                          }}
                        />
                      );
                    })}
                    {tooltip.visible && tooltip.dayIdx === sourceDay && <Tooltip tooltip={tooltip} containerWidth={containerWidth} classes={classes} />}
                  </div>
                </div>
                <div style={{ position: 'relative', height: 28, marginTop: 2 }}>
                  {[0, 4, 8, 12, 16, 20, 24].map((t) => {
                    let num;
                    let suffix = '';
                    if (t === 0 || t === 24) { num = '12'; suffix = 'am'; }
                    else if (t === 12) { num = '12'; suffix = 'pm'; }
                    else if (t < 12) { num = t + ''; }
                    else { num = (t - 12) + ''; }
                    const transform = t === 0 ? 'translateX(0)' : t === 24 ? 'translateX(-100%)' : 'translateX(-50%)';
                    return (
                      <div key={t} style={{ position: "absolute", left: timeToPx(t), transform }}>
                        <div className={classes.xAxisLabel}><div>{num}{suffix && <div>{suffix}</div>}</div></div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {!isReadOnlyMode && <div style={{ marginBottom: 16 }} />}
            <div className={`${selectedDays.length === 0 ? classes.noDaySelected : ''} ${classes.editWrapper}`}>
              {/* ✅ BLOCK CARDS */}
              {mode === 'reorder' ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sourceBlocks.map(b => b.id)} strategy={horizontalListSortingStrategy}>
                    <div
                      ref={editBlocksRef}
                      className={`${classes.editBlocksRow} ${sourceBlockCount === 1 && mode !== 'add' ? classes.disableEditBlock : ""}`}
                    >
                      {sourceBlocks.map((block, index) => (
                        <SortableBlockCard
                          key={block.id}
                          block={block}
                          index={index}
                          mode={mode}
                          selectedBlockIndex={selectedBlockIndex}
                          setSelectedBlockIndex={setSelectedBlockIndex}
                          deleteSingleScene={deleteSingleScene}
                          classes={classes}
                          presets={localPresets}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div
                  ref={editBlocksRef}
                  className={`${classes.editBlocksRow} ${mode === 'delete' ? classes.deleteEditRow : ''} ${sourceBlockCount === 1 && mode !== 'add' ? classes.disableEditBlock : ""}`}
                >
                  {sourceBlocks.map((block, index) => (
                    <SortableBlockCard
                      key={block.id}
                      block={block}
                      index={index}
                      mode={mode}
                      selectedBlockIndex={selectedBlockIndex}
                      setSelectedBlockIndex={setSelectedBlockIndex}
                      deleteSingleScene={deleteSingleScene}
                      classes={classes}
                      presets={localPresets}
                    />
                  ))}
                </div>
              )}

              <div className={`${classes.editTimeTimers} ${mode === 'delete' ? classes.inDeleteMode : ''} ${selectedBlockIndex !== null ? '' : classes.noDaySelected}`} style={{visibility: selectedBlockIndex !== null ? 'visible' : 'hidden'}}>
                <div ref={startDateRef} className={classes.timersField}>
                  <div className={classes.timersFieldLabel}>{I18n.t("mobile.iqAirScheduler.from")}</div>
                  <span onClick={() => setShowStartDropdown(true)}>{formatHHMM(selectedStartTime)}</span>
                  {showStartDropdown && <Dropdown
                    classes={classes}
                    classname='timePicker'
                    anchorRef={startDateRef}
                    onClose={() => { setShowStartDropdown(false); }}
                    onSelect={(val) => {
                      setShowStartDropdown(false);
                      updateBlockTime("start", formatSchedulerTime(val));

                    }}
                    data={generateHourArray()}
                    selectedValue={selectedSourceBlock?.start}
                    isDarkMode={isDarkMode}
                  />}
                </div>


                <div ref={endDateRef} className={classes.timersField}>
                  <div className={classes.timersFieldLabel}>{I18n.t("mobile.iqAirScheduler.to")}</div>
                  <span onClick={() => setShowEndDropdown(true)}>{formatHHMM(selectedEndTime)}</span>
                  {showEndDropdown && <Dropdown
                    classes={classes}
                    classname='timePicker'
                    anchorRef={endDateRef}
                    onClose={() => { setShowEndDropdown(false); }}
                    onSelect={(val) => {
                      setShowEndDropdown(false);
                      const endVal = formatSchedulerTime(val);
                      updateBlockTime("end", endVal === 0 ? 24 : endVal);

                    }}
                    data={generateHourArray()}
                    selectedValue={selectedSourceBlock?.end}
                    isDarkMode={isDarkMode}
                  />}
                </div>
              </div>

            </div>
            {/* Applies to: day selection at bottom (only in edit/popup mode) */}
            {!isReadOnlyMode && <div style={{ marginTop: 16 }}>
              <div className={classes.pageTitle}>{I18n.t('mobile.iqAirScheduler.appliesTo', { defaultValue: 'Applies to' })}</div>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 8, marginTop: 8, marginBottom: 8 }}>
                {DAYS.map((d, dayIdx) => {
                  const isSelected = selectedDays.includes(dayIdx);
                  return (
                    <div
                      key={dayIdx}
                      onClick={() => {
                        setSelectedDays((prev) => {
                          let updated;
                          if (prev.includes(dayIdx)) {
                            updated = prev.filter((x) => x !== dayIdx);
                          } else {
                            updated = [...prev, dayIdx];
                          }
                          if (!updated.length) {
                            setSourceDay(null);
                          } else if (!sourceDay || !updated.includes(sourceDay)) {
                            setSourceDay(updated[0]);
                          }
                          return updated;
                        });
                      }}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        background: isSelected ? '#01B5DE' : '#dfdfdf',
                        color: isSelected ? '#fff' : '#545456',
                        fontWeight: 400,
                        fontSize: 12,
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      {d}
                    </div>
                  );
                })}
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}
                onClick={() => {
                  if (selectedDays.length === 7) {
                    setSelectedDays([]);
                    setSourceDay(null);
                  } else {
                    const allDays = [0, 1, 2, 3, 4, 5, 6];
                    setSelectedDays(allDays);
                    if (sourceDay === null) setSourceDay(0);
                  }
                }}
              >
                <Checkbox
                  checked={selectedDays.length === 7}
                  style={{ color: selectedDays.length === 7 ? '#01B5DE' : '#6b7073', padding: 0, width: 16, height: 16 }}
                  size="small"
                />
                <span style={{ fontSize: 12, fontWeight: 400, color: isDarkMode ? '#fff' : '#545456' }}>
                  {I18n.t('mobile.iqAirScheduler.everyday', { defaultValue: 'Everyday' })}
                </span>
              </div>
            </div>}

            {/* Cancel / Save buttons (only in edit/popup mode) */}
            {!isReadOnlyMode && (
              <div style={{ display: 'flex', gap: 12, marginTop: 24, paddingBottom: isiOS() ? 'calc(16px + env(safe-area-inset-bottom, 0px))' : 16 }}>
                <button
                  type="button"
                  onClick={() => {
                    if (onClosePopup) onClosePopup();
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 0',
                    border: '2px solid #01B5DE',
                    borderRadius: 8,
                    background: 'transparent',
                    color: '#01B5DE',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {I18n.t('mobile.iqAirScheduler.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const base = originalData ? { ...originalData } : {};
                    for (let i = 0; i < 7; i++) {
                      if (selectedDays.includes(i)) {
                        base[i] = data[i] || [];
                      } else if (initialSelectedDays.includes(i)) {
                        base[i] = [];
                      }
                    }
                    // Check if any selected days already have schedules in originalData (override scenario)
                    const daysWithExisting = selectedDays.filter(d =>
                      !initialSelectedDays.includes(d) &&
                      Array.isArray(originalData?.[d]) &&
                      originalData[d].length > 0
                    );
                    if (daysWithExisting.length > 0) {
                      pendingSaveDataRef.current = base;
                      preOverrideSelectedDaysRef.current = [...selectedDays];
                      preOverrideDataRef.current = JSON.parse(JSON.stringify(data));
                      setOverrideDays(daysWithExisting);
                      setShowOverridePopup(true);
                      return;
                    }
                    if (typeof onSave === 'function') {
                      onSave(base);
                    } else {
                      if (typeof onScheduleDataChange === 'function') {
                        onScheduleDataChange(base);
                      }
                      if (typeof handleClick === 'function') handleClick();
                      else if (onClosePopup) onClosePopup();
                    }
                  }}
                  disabled={selectedDays.length === 0 || (!isAddModeProp && !hasChanges)}
                  style={{
                    flex: 1,
                    padding: '12px 0',
                    border: 'none',
                    borderRadius: 8,
                    background: (selectedDays.length === 0 || (!isAddModeProp && !hasChanges)) ? '#BABEC3' : '#01B5DE',
                    color: (selectedDays.length === 0 || (!isAddModeProp && !hasChanges)) ? '#6B7073' : '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: (selectedDays.length === 0 || (!isAddModeProp && !hasChanges)) ? 'default' : 'pointer',
                  }}
                >
                  {I18n.t('mobile.iqAirScheduler.save', { defaultValue: 'Save' })}
                </button>
              </div>
            )}

          </div>

          {showOverridePopup && (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.4)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <div style={{
                backgroundColor: isDarkMode ? '#2c2c2e' : '#fff',
                borderRadius: 12,
                padding: 24,
                width: '85%',
                maxWidth: 360,
                textAlign: 'center',
              }}>
                <div style={{ marginBottom: 16 }}>
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="22" stroke={isDarkMode ? '#aaa' : '#666'} strokeWidth="2" fill="none" />
                    <text x="24" y="32" textAnchor="middle" fontSize="28" fontWeight="600" fill={isDarkMode ? '#aaa' : '#666'}>!</text>
                  </svg>
                </div>
                <div style={{ fontSize: 14, color: isDarkMode ? '#e5e5e5' : '#333', marginBottom: 8 }}>
                  {I18n.t('mobile.iqAirScheduler.overrideMessage', { defaultValue: 'This action will override the schedule(s) for' })}
                </div>
                <div style={{ fontSize: 14, color: isDarkMode ? '#fff' : '#333', fontWeight: 600, marginBottom: 8 }}>
                  {overrideDays.map(d => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][d]).join(', ')}.
                </div>
                <div style={{ fontSize: 14, color: isDarkMode ? '#e5e5e5' : '#333', marginBottom: 24 }}>
                  {I18n.t('mobile.iqAirScheduler.proceedConfirm', { defaultValue: 'Do you want to proceed?' })}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowOverridePopup(false);
                      pendingSaveDataRef.current = null;
                      preOverrideSelectedDaysRef.current = null;
                      preOverrideDataRef.current = null;
                      // Revert all changes: days, data, and time edits back to original
                      const revertedData = JSON.parse(JSON.stringify(originalScheduleDataRef.current));
                      setSelectedDays(initialSelectedDays);
                      setSourceDay(initialSelectedDays.length > 0 ? initialSelectedDays[0] : null);
                      setData(revertedData);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 0',
                      border: '1px solid #01B5DE',
                      borderRadius: 10,
                      backgroundColor: isDarkMode ? 'transparent' : '#fff',
                      color: '#01B5DE',
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {I18n.t('mobile.iqAirScheduler.cancel', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    onClick={() => {
                      setShowOverridePopup(false);
                      const saveData = pendingSaveDataRef.current;
                      pendingSaveDataRef.current = null;
                      if (!saveData) return;
                      if (typeof onSave === 'function') {
                        onSave(saveData);
                      } else {
                        if (typeof onScheduleDataChange === 'function') {
                          onScheduleDataChange(saveData);
                        }
                        if (typeof handleClick === 'function') handleClick();
                        else if (onClosePopup) onClosePopup();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 0',
                      border: 'none',
                      borderRadius: 10,
                      backgroundColor: '#01B5DE',
                      color: '#fff',
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {I18n.t('mobile.iqAirScheduler.proceed', { defaultValue: 'Proceed' })}
                  </button>
                </div>
              </div>
            </div>
          )}
      </>
      }
    </div>
    )}
    </>
  );
}